import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { getIntlProps, type IntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { useTranslation } from 'react-i18next';
import React, { useEffect } from 'react';
import { GcssLayout } from '@/components/layout/GcssLayout';
import { ContentContainer } from '@/components/layout';
import { useGcssValuesToSearch } from '@/sections/gcss/hooks/useGcssSearch';
import { Search } from '@/sections/gcss/Search';
import { LINKS } from '@/constants/links';
import { getMainpageData, MainpageData } from '@/sections/gcss/functions/getMainpageData';
import { Headline } from '@/components/typography';
import { ProductCard } from '@/sections/gcss/ProductCard';
import { Categories } from '@/sections/gcss/Categories';
import EshopBoxes from '@/components/boxes/EshopBox/EshopBoxes';
import { useDiscountsSearch } from '@/sections/discounts/hooks/useDiscountsSearch';
import { SearchResults } from '@/sections/discounts/SearchResults';
import { Button } from '@/components/buttons';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import { Discover } from '@/sections/gcss/Discover';
import { MoreInfo } from '@/sections/gcss/MoreInfo';
import { logProductsEvent } from '@/analytics/logProductsEvent';
import { isAllowedRoute } from '@/lang/pagesConfig';
interface Props extends IntlProps {
  data: MainpageData;
}
export default function Page(props) {
  const $ = _c(72);
  usePageView("gcss-homepage");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["core", "gcss"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    data
  } = props;
  const {
    formValues,
    searchForm
  } = useGcssValuesToSearch();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      limit: 4
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    query
  } = useDiscountsSearch(t1);
  let t2;
  let t3;
  if ($[2] !== data.products) {
    t2 = () => {
      if (data.products) {
        logProductsEvent(data.products, "main", "recommended", undefined);
      }
    };
    t3 = [data.products];
    $[2] = data.products;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="bgCircle absolute top-0 right-0 h-[25rem] w-[25rem] translate-x-[40%] -translate-y-1/2 rotate-180 lg:h-[55.25rem] lg:w-[55.25rem] lg:translate-x-[54%] lg:-translate-y-[40%] lg:rotate-[195deg]" />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t) {
    t5 = t("gcss:homepage.title");
    $[6] = t;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <h1 className="text-center text-4xl font-bold lg:text-8xl">{t5}</h1>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t) {
    t7 = t("gcss:homepage.subtitle");
    $[10] = t;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t7) {
    t8 = <p className="mt-6 text-center text-sm lg:mt-8 lg:text-xl">{t7}</p>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t6 || $[15] !== t8) {
    t9 = <div className="mx-auto max-w-192 px-2">{t6}{t8}</div>;
    $[14] = t6;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Search />;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t) {
    t11 = t("gcss:homepage.products");
    $[18] = t;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11) {
    t12 = <Headline as="h4" className="mt-12 mb-6 md:mt-14 md:mb-12 md:text-center">{t11}</Headline>;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== data.products) {
    t13 = data.products.map(_temp);
    $[22] = data.products;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t13) {
    t14 = <section className="mb-20 grid grid-cols-1 gap-8 sm:grid-cols-2 md:mb-32 md:grid-cols-3 lg:grid-cols-4">{t13}</section>;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t) {
    t15 = t("gcss:homepage.categories");
    $[26] = t;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t15) {
    t16 = <Headline as="h4" className="mb-6 md:mb-12 md:text-center">{t15}</Headline>;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== data.categories) {
    t17 = <Categories className="mb-20 md:mb-32" variant="collapsible" subcategories={data.categories} />;
    $[30] = data.categories;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t) {
    t18 = t("gcss:homepage.eshops");
    $[32] = t;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t18) {
    t19 = <Headline as="h4" className="mt-12 mb-6 md:mt-14 md:mb-12 md:text-center">{t18}</Headline>;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== data.eshops) {
    t20 = <section className="mb-20 md:mb-32"><EshopBoxes boxSize="sm" eshops={data.eshops} /></section>;
    $[36] = data.eshops;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t) {
    t21 = t("gcss:homepage.discounts.title");
    $[38] = t;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t21) {
    t22 = <Headline as="h4" className="mt-12 mb-2 md:mt-14 md:mb-4 md:text-center">{t21}</Headline>;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = [];
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== query) {
    t24 = <SearchResults emptyCategories={t23} query={query} loadMoreLimit={0} />;
    $[43] = query;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = LINKS();
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  const t26 = t25.discounts;
  let t27;
  if ($[46] !== t) {
    t27 = t("gcss:homepage.discounts.button");
    $[46] = t;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] !== t27) {
    t28 = <Button variant="secondary" analyticsParam="gcssHomepageDiscounts" testId="gcssHomepageDiscountsButton" to={t26}>{t27}</Button>;
    $[48] = t27;
    $[49] = t28;
  } else {
    t28 = $[49];
  }
  let t29;
  if ($[50] !== t24 || $[51] !== t28) {
    t29 = <section className="mb-20 flex flex-col items-center gap-6 md:mb-32 md:gap-12">{t24}{t28}</section>;
    $[50] = t24;
    $[51] = t28;
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  let t30;
  if ($[53] !== t12 || $[54] !== t14 || $[55] !== t16 || $[56] !== t17 || $[57] !== t19 || $[58] !== t20 || $[59] !== t22 || $[60] !== t29) {
    t30 = <ContentContainer className="mt-12 px-4 lg:mt-16 lg:px-0">{t10}{t12}{t14}{t16}{t17}{t19}{t20}{t22}{t29}</ContentContainer>;
    $[53] = t12;
    $[54] = t14;
    $[55] = t16;
    $[56] = t17;
    $[57] = t19;
    $[58] = t20;
    $[59] = t22;
    $[60] = t29;
    $[61] = t30;
  } else {
    t30 = $[61];
  }
  let t31;
  if ($[62] !== t30 || $[63] !== t9) {
    t31 = <div className="relative overflow-hidden bg-blue-100 pt-32 pb-12 md:px-4 lg:pt-64">{t4}<ContentContainer>{t9}{t30}</ContentContainer></div>;
    $[62] = t30;
    $[63] = t9;
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  let t32;
  let t33;
  let t34;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <SectionDelimiter color="bg-blue-50" background="bg-blue-100" />;
    t33 = <Discover />;
    t34 = <MoreInfo />;
    $[65] = t32;
    $[66] = t33;
    $[67] = t34;
  } else {
    t32 = $[65];
    t33 = $[66];
    t34 = $[67];
  }
  let t35;
  if ($[68] !== formValues || $[69] !== searchForm || $[70] !== t31) {
    t35 = <GcssLayout searchForm={searchForm} formValues={formValues} isHomepage={true}>{t31}{t32}{t33}{t34}</GcssLayout>;
    $[68] = formValues;
    $[69] = searchForm;
    $[70] = t31;
    $[71] = t35;
  } else {
    t35 = $[71];
  }
  return t35;
}
function _temp(product, index) {
  return <ProductCard key={index} product={product} category="main" subcategory="recommended" page={undefined} />;
}
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('srovnavac-produktu');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  const data = await getMainpageData();
  return {
    props: {
      data,
      ...(await getIntlProps(ctx, ['core', 'gcss', 'discounts']))
    }
  };
}) satisfies GetStaticProps<Props>;